import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';
export const THEME_STORAGE_KEY = 'ssemr-hub-theme';

export function getSavedTheme(): Theme | null {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    // Return null if localStorage is inaccessible
  }
  return null;
}

export function getSystemTheme(): Theme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyThemeToDOM(theme: Theme | null) {
  if (typeof document === 'undefined') return;
  if (theme === 'light' || theme === 'dark') {
    document.documentElement.setAttribute('data-theme', theme);
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
}

// Apply saved theme immediately on module load to prevent theme flashing
applyThemeToDOM(getSavedTheme());

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    const saved = getSavedTheme();
    return saved ?? getSystemTheme();
  });

  useEffect(() => {
    const saved = getSavedTheme();
    applyThemeToDOM(saved);
  }, [theme]);

  useEffect(() => {
    if (!window.matchMedia) return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (!getSavedTheme()) {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    const nextTheme: Theme = theme === 'light' ? 'dark' : 'light';
    try {
      localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
    } catch {
      // Ignore write errors
    }
    applyThemeToDOM(nextTheme);
    setThemeState(nextTheme);
  };

  return { theme, toggleTheme };
}
